import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";

const AuthModalContext = createContext(null);

export function AuthModalProvider({ children }) {
  const { isAuthenticated, isCustomer } = useAuth();
  const [open, setOpen] = useState(false);
  const afterAuthRef = useRef(null);
  const wasAuthRef = useRef(isAuthenticated && isCustomer);

  const openAuthModal = useCallback((options = {}) => {
    const cb = typeof options.onSuccess === "function" ? options.onSuccess : null;
    // Fallback: if already authenticated, fire the callback immediately without opening modal
    if (isAuthenticated && isCustomer) {
      if (cb) setTimeout(() => { try { cb(); } catch { /* noop */ } }, 0);
      return;
    }
    afterAuthRef.current = cb;
    setOpen(true);
  }, [isAuthenticated, isCustomer]);

  const closeAuthModal = useCallback(() => {
    setOpen(false);
    afterAuthRef.current = null;
  }, []);

  // Fire onSuccess once the user transitions to authenticated while modal was open
  useEffect(() => {
    const nowAuth = isAuthenticated && isCustomer;
    const wasAuth = wasAuthRef.current;
    wasAuthRef.current = nowAuth;
    if (nowAuth && !wasAuth) {
      setOpen(false);
      const cb = afterAuthRef.current;
      afterAuthRef.current = null;
      if (typeof cb === "function") {
        setTimeout(() => { try { cb(); } catch { /* noop */ } }, 0);
      }
    }
  }, [isAuthenticated, isCustomer]);

  return (
    <AuthModalContext.Provider value={{ open, openAuthModal, closeAuthModal }}>
      {children}
    </AuthModalContext.Provider>
  );
}

export const useAuthModal = () => useContext(AuthModalContext) || {
  open: false, openAuthModal: () => {}, closeAuthModal: () => {},
};
